import React, { useRef } from 'react';
import { Paper, CircularProgress } from '@material-ui/core';
import TablePagination from '../../../reports/screens/InventoryOrderReport/TablePagination';
import DataTableHeader from './DataTableHeader';
import DataTableBody from './DataTableBody';
import { ColumnDef, SortConfig } from './types';

const MAX_TABLE_HEIGHT = 'calc(100vh - 350px)';
const EMPTY_STATE_MIN_HEIGHT = 160;
const HEADER_HEIGHT = 45;
const ROW_HEIGHT = 52;

export interface DataTableProps<T> {
    columns: ColumnDef<T>[];
    rows: T[];
    getRowId: (row: T) => string | number;
    loading?: boolean;
    page: number;
    pageSize: number;
    totalCount: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    sortConfig?: SortConfig | null;
    onSort?: (field: string) => void;
    emptyMessage?: string;
}

/**
 * Virtualized, server-paginated data table used across the Expiring Stock module screens.
 * Mirrors the architecture (Paper + sticky header + @tanstack/react-virtual body + footer
 * pagination) used by the Inventory Replenishment Hub's virtual table, adapted for
 * read-only, column-config-driven rows instead of inline-editable spreadsheet cells.
 */
function DataTable<T>({
    columns,
    rows,
    getRowId,
    loading = false,
    page,
    pageSize,
    totalCount,
    onPageChange,
    onPageSizeChange,
    sortConfig,
    onSort,
    emptyMessage = 'No data',
}: DataTableProps<T>) {
    const parentRef = useRef<HTMLDivElement>(null);

    return (
        <Paper
            elevation={2}
            style={{
                width: '100%',
                maxWidth: '100%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
            }}
        >
            <div
                ref={parentRef}
                style={{
                    minHeight: loading || rows.length === 0 ? EMPTY_STATE_MIN_HEIGHT : undefined,
                    maxHeight: MAX_TABLE_HEIGHT,
                    minWidth: 0,
                    width: '100%',
                    height: HEADER_HEIGHT + rows.length * ROW_HEIGHT,
                    overflow: 'auto',
                    position: 'relative',
                }}
            >
                <div style={{ width: '100%', minWidth: '100%' }}>
                    <div style={{ position: 'sticky', top: 0, zIndex: 3, backgroundColor: '#fafafa' }}>
                        <DataTableHeader columns={columns} sortConfig={sortConfig} onSort={onSort} />
                    </div>
                    {loading ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48 }}>
                            <CircularProgress />
                        </div>
                    ) : rows.length === 0 ? (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 48,
                                color: '#666',
                            }}
                        >
                            {emptyMessage}
                        </div>
                    ) : (
                        <DataTableBody rows={rows} columns={columns} getRowId={getRowId} parentRef={parentRef} />
                    )}
                </div>
            </div>
            <TablePagination
                page={page}
                pageSize={pageSize}
                totalCount={totalCount}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
            />
        </Paper>
    );
}

export default DataTable;
